'use client'

import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'

type Slide = {
  src: string
  alt: string
}

type Props = {
  slides: Slide[]
  autoplayMs?: number
}

export default function TestimonialCarousel({ slides, autoplayMs = 3000 }: Props) {
  const total = slides.length
  // Duplicate slides for seamless looping: [...slides, ...slides, ...slides]
  const cloned = [...slides, ...slides, ...slides]
  const [index, setIndex] = useState(total) // start at the real first slide
  const [animated, setAnimated] = useState(true)
  const transitioning = useRef(false)

  const visibleDesktop = 3
  const gap = 24 // px, keep in sync with CSS gap below

  const advance = useCallback((dir: 1 | -1) => {
    if (transitioning.current) return
    setAnimated(true)
    setIndex((prev) => prev + dir)
  }, [])

  // Auto-play
  useEffect(() => {
    const id = setInterval(() => advance(1), autoplayMs)
    return () => clearInterval(id)
  }, [advance, autoplayMs])

  // Seamless loop: when we reach a clone, snap without animation
  useEffect(() => {
    if (index >= total * 2) {
      transitioning.current = true
      const id = setTimeout(() => {
        setAnimated(false)
        setIndex(total)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            transitioning.current = false
          })
        })
      }, 400)
      return () => clearTimeout(id)
    }
    if (index < total) {
      transitioning.current = true
      const id = setTimeout(() => {
        setAnimated(false)
        setIndex(total * 2 - 1)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            transitioning.current = false
          })
        })
      }, 400)
      return () => clearTimeout(id)
    }
  }, [index, total])

  // Re-enable animation after snap
  useEffect(() => {
    if (!animated) {
      const id = requestAnimationFrame(() => setAnimated(true))
      return () => cancelAnimationFrame(id)
    }
  }, [animated])

  return (
    <div className="relative w-full overflow-hidden">
      {/* Track */}
      <div
        className="flex"
        style={{
          gap: `${gap}px`,
          transform: `translateX(calc(-${index} * (100% / ${visibleDesktop}) - ${index} * ${gap / visibleDesktop}px + ${gap / visibleDesktop}px))`,
          transition: animated ? 'transform 0.4s ease' : 'none',
        }}
      >
        {cloned.map((slide, i) => (
          <div
            key={i}
            className="shrink-0"
            style={{ width: `calc((100% - ${gap * (visibleDesktop - 1)}px) / ${visibleDesktop})` }}
          >
            <Image
              src={slide.src}
              alt={slide.alt}
              width={400}
              height={300}
              // Three slides across at every breakpoint (see `visibleDesktop`),
              // so each is roughly a third of the track minus the gaps.
              sizes="(min-width: 1280px) 390px, 33vw"
              className="h-auto w-full rounded-md"
              draggable={false}
            />
          </div>
        ))}
      </div>

      {/* Prev / Next — theme-lock: overlaid on photos, so black scrim + white glyph in both themes */}
      <button
        type="button"
        aria-label="Previous testimonial"
        onClick={() => advance(-1)}
        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition hover:bg-black/80"
      >
        ‹
      </button>
      <button
        type="button"
        aria-label="Next testimonial"
        onClick={() => advance(1)}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white transition hover:bg-black/80"
      >
        ›
      </button>

      {/* Dots */}
      <div className="mt-4 flex justify-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => {
              setAnimated(true)
              setIndex(total + i)
            }}
            className={`h-2 w-2 rounded-full transition-colors ${
              (index - total + total) % total === i ? 'bg-accent' : 'bg-fg/30'
            }`}
          />
        ))}
      </div>
    </div>
  )
}
